import { z } from 'zod';
import { GYM_MODULE_KEYS, WEBSITE_BLOCK_VARIANTS, WEBSITE_SECTION_TYPES, WEBSITE_TEMPLATES } from '@bulk/types';
import { idSchema, patchOf, publicAssetSchema } from './common';
import { publicPlanSchema } from './plans';
import { classSchema, sessionSchema } from './classes';
import { campaignStateSchema, publicCampaignsSchema } from './campaigns';
import { publicMenuCategorySchema } from './menu';
import { publicJobOpeningSchema } from './recruiting';
import { brandingSchema, openingHoursSchema } from './gyms';
import { publicAwardSchema } from './recognition';
import { blockStyleSchema, linkSchema, localizedText, sectionContentSchemas } from './website-blocks';
import { siteDesignSchema } from './website-design';
import { PAGE_SLUG_PATTERN, isReservedSlug } from './website-slugs';
import { REDIRECT_LIMIT, redirectSchema } from './website-redirects';
import { MEMBER_HEADER_VARIANTS, MEMBER_HOME_CARDS, memberHomeSchema, quickLinkSchema } from './member-home';

export * from './website-blocks';
export * from './website-slugs';
export * from './website-design';
export * from './website-redirects';
export * from './member-home';

export const websiteSectionSchema = z.object({
  id: z.string().min(1).max(60),
  type: z.enum(WEBSITE_SECTION_TYPES),
  enabled: z.boolean().default(true),
  variant: z.string().trim().max(40).default('default'),
  style: blockStyleSchema.default(() => blockStyleSchema.parse({})),
  content: z.record(z.string(), z.unknown()),
});
export type WebsiteSection = z.infer<typeof websiteSectionSchema>;

/** A variant the block does not have renders as its first one, so a stored site never breaks over it. */
export function blockVariant(section: Pick<WebsiteSection, 'type' | 'variant'>): string {
  const variants = WEBSITE_BLOCK_VARIANTS[section.type];
  return variants.includes(section.variant) ? section.variant : (variants[0] ?? 'default');
}

export const seoSchema = z.object({
  title: localizedText.default({}),
  description: localizedText.default({}),
  ogImageAssetId: idSchema.nullable().default(null),
  canonicalUrl: z.string().max(500).nullable().default(null),
  robotsIndex: z.boolean().default(true),
});
export type Seo = z.infer<typeof seoSchema>;

export const socialLinksSchema = z.object({
  facebook: z.string().max(300).nullable().default(null),
  instagram: z.string().max(300).nullable().default(null),
  tiktok: z.string().max(300).nullable().default(null),
  youtube: z.string().max(300).nullable().default(null),
});

export const pageSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(63)
  .regex(PAGE_SLUG_PATTERN, 'Lowercase letters, digits and dashes only')
  .refine((slug) => !isReservedSlug(slug), {
    message: 'That address is used by the site itself; choose another',
  });

const websitePageShape = {
  id: z.string().min(1).max(60),
  slug: pageSlugSchema,
  slugLocked: z.boolean().default(false).describe('Set when a platform admin chose the address by hand; it then no longer follows the title'),
  title: localizedText,
  navLabel: localizedText.default({}),
  showInNav: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  sections: z.array(websiteSectionSchema).default([]),
  seo: seoSchema.default(() => seoSchema.parse({})),
};

export const websitePageSchema = z.object(websitePageShape);
export type WebsitePage = z.infer<typeof websitePageSchema>;

export const savedBlockSchema = z.object({
  id: z.string().min(1).max(60),
  name: z.string().trim().min(1).max(80),
  block: websiteSectionSchema,
});
export type SavedBlock = z.infer<typeof savedBlockSchema>;

const websiteContentShape = {
  template: z.enum(WEBSITE_TEMPLATES).default('classic'),
  /** The home page. Kept at the top level so every site written before pages existed still parses. */
  sections: z.array(websiteSectionSchema).default([]),
  /** Pages beside the home page, each at its own address. */
  pages: z.array(websitePageSchema).max(50).default([]),
  seo: seoSchema.default(() => seoSchema.parse({})),
  social: socialLinksSchema.default(() => socialLinksSchema.parse({})),
  footerText: localizedText.default({}),
  navLinks: z.array(linkSchema).default([]),
  /** Blocks the content team saved to insert again elsewhere; a copy is inserted, never a link to the original. */
  savedBlocks: z.array(savedBlockSchema).max(100).default([]),
  /** Old addresses, by path or `#/` hash route, sent on to where their content lives now. Live on publish. */
  redirects: z.array(redirectSchema).max(REDIRECT_LIMIT).default([]),
  /** How the member home is arranged, within what the design allows. Live on publish, like the site. */
  memberHome: memberHomeSchema.default(() => memberHomeSchema.parse({})),
};

export const websiteContentSchema = z.object(websiteContentShape).refine((c) => new Set(c.pages.map((p) => p.slug)).size === c.pages.length, {
  message: 'Two pages cannot share the same address',
  path: ['pages'],
});
export type WebsiteContent = z.infer<typeof websiteContentSchema>;

function keep<T>(schema: z.ZodType<T>, value: unknown): T | undefined {
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Everything in a stored document that still parses: sections and pages that fail today's rules are dropped
 * one by one, a later page at an address already taken goes, and any other field that fails falls back to its
 * default. The public site renders this rather than going down when a rule tightens under a saved site.
 */
export function salvageWebsiteContent(raw: unknown): WebsiteContent {
  const whole = websiteContentSchema.safeParse(raw);
  if (whole.success) return whole.data;
  const doc = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const sections = (value: unknown) => (Array.isArray(value) ? value.flatMap((s) => keep(websiteSectionSchema, s) ?? []) : []);
  const slugs = new Set<string>();
  const pages = (Array.isArray(doc['pages']) ? doc['pages'] : []).flatMap((p) => {
    const page = p !== null && typeof p === 'object' ? keep(websitePageSchema, { ...(p as Record<string, unknown>), sections: sections((p as Record<string, unknown>)['sections']) }) : undefined;
    if (!page || slugs.has(page.slug)) return [];
    slugs.add(page.slug);
    return [page];
  });
  return websiteContentSchema.parse({
    template: keep(z.enum(WEBSITE_TEMPLATES), doc['template']),
    sections: sections(doc['sections']),
    pages: pages.slice(0, 50),
    seo: keep(seoSchema, doc['seo']),
    social: keep(socialLinksSchema, doc['social']),
    footerText: keep(localizedText, doc['footerText']),
    navLinks: Array.isArray(doc['navLinks']) ? doc['navLinks'].flatMap((l) => keep(linkSchema, l) ?? []) : [],
    savedBlocks: Array.isArray(doc['savedBlocks']) ? doc['savedBlocks'].flatMap((b) => keep(savedBlockSchema, b) ?? []).slice(0, 100) : [],
    redirects: Array.isArray(doc['redirects']) ? doc['redirects'].flatMap((r) => keep(redirectSchema, r) ?? []).slice(0, REDIRECT_LIMIT) : [],
    memberHome: keep(memberHomeSchema, doc['memberHome']),
  });
}

export interface BlockIssue {
  path: string;
  message: string;
}

/** The first way a block breaks its type's rules (content or variant), with the path an editor can follow; null when it is fine. */
export function blockIssue(section: WebsiteSection, path: string): BlockIssue | null {
  if (section.variant !== 'default' && !WEBSITE_BLOCK_VARIANTS[section.type].includes(section.variant)) {
    return { path: `${path}.variant`, message: `${section.type} has no variant "${section.variant}"` };
  }
  const parsed = sectionContentSchemas[section.type].safeParse(section.content);
  if (parsed.success) return null;
  const issue = parsed.error.issues[0];
  const where = issue && issue.path.length > 0 ? `.${issue.path.map(String).join('.')}` : '';
  return { path: `${path}.content${where}`, message: issue?.message ?? 'Invalid content' };
}

export function documentBlocks(content: Pick<WebsiteContent, 'sections' | 'pages' | 'savedBlocks'>): Array<{ section: WebsiteSection; path: string }> {
  return [
    ...content.sections.map((section, i) => ({ section, path: `sections.${i}` })),
    ...content.pages.flatMap((page, p) => page.sections.map((section, i) => ({ section, path: `pages.${p}.sections.${i}` }))),
    ...content.savedBlocks.map((saved, i) => ({ section: saved.block, path: `savedBlocks.${i}.block` })),
  ];
}

export const websiteQuarantineSchema = z.object({
  at: z.string(),
  reason: z.string().describe('The first rule the set-aside draft broke'),
  canRestore: z.boolean().describe('A published version exists to restore'),
});

export const websiteSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  draft: websiteContentSchema,
  published: websiteContentSchema.nullable(),
  publishedAt: z.string().nullable(),
  draftUpdatedAt: z.string(),
  hasUnpublishedChanges: z.boolean(),
  quarantine: websiteQuarantineSchema.nullable().describe('Set when the saved draft no longer passed the rules and was set aside; the draft is then a fresh one'),
});
export type WebsiteDto = z.infer<typeof websiteSchema>;

/** A page as the editor sends it. The server gives it its address, so `slug` and `slugLocked` are read and ignored. */
export const websitePageDraftSchema = z.object({
  ...websitePageShape,
  slug: z.string().trim().max(100).optional().describe('Ignored: the address is generated from the title and frozen once published'),
  slugLocked: z.boolean().optional().describe('Ignored: only a platform admin sets an address by hand, through PUT /website/pages/{pageId}/address'),
});
export type WebsitePageDraft = z.infer<typeof websitePageDraftSchema>;

export const updateWebsiteDraftSchema = patchOf(z.object(websiteContentShape)).extend({
  pages: z.array(websitePageDraftSchema).max(50).optional(),
});
export type UpdateWebsiteDraftInput = z.infer<typeof updateWebsiteDraftSchema>;

export const setPageAddressSchema = z.object({
  slug: pageSlugSchema.nullable().describe('null hands the address back to the title'),
});
export type SetPageAddressInput = z.infer<typeof setPageAddressSchema>;

export const contactFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.email(),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().min(1).max(2000),
});
export type ContactFormInput = z.infer<typeof contactFormSchema>;

export const publicLocationSchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()).describe('The name per locale; a locale missing here shows name'),
  address: z.string().nullable(),
  city: z.string().nullable(),
  phone: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type PublicLocationDto = z.infer<typeof publicLocationSchema>;

export const publicSiteSchema = z.object({
  gym: z.object({
    id: idSchema,
    name: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    currency: z.string(),
    timezone: z.string(),
    defaultLocale: z.string(),
    branding: z.record(z.string(), z.unknown()),
    logoUrl: z.string().nullable(),
    onlinePurchases: z.boolean(),
    purchasesPausedMessage: z.record(z.string(), z.string()),
    openingHours: openingHoursSchema.describe('Keyed MON…SUN; a missing or null day is closed'),
  }),
  content: websiteContentSchema,
  design: siteDesignSchema.nullable().describe("The gym's design, built by Bulk; null draws the plain look"),
  plans: z.array(publicPlanSchema),
  trainers: z.array(
    z.object({
      id: idSchema,
      firstName: z.string(),
      lastName: z.string(),
      firstNameLocalized: z.record(z.string(), z.string()).describe('The name per locale; a locale missing here shows firstName'),
      lastNameLocalized: z.record(z.string(), z.string()),
      bio: z.string().nullable(),
      bioLocalized: z.record(z.string(), z.string()),
      specialties: z.array(z.string()),
      specialtyIds: z.array(idSchema),
      specialtiesLocalized: z.array(z.record(z.string(), z.string())).describe('One entry per speciality: its name per locale, English under "en"'),
      photo: publicAssetSchema.nullable().describe('The website photo, or the staff photo when there is none'),
    }),
  ),
  awards: z.array(publicAwardSchema).describe("PUBLIC awards only, newest first; empty while the recognition module is off. A staff-only award is never in this payload"),
  classes: z.array(classSchema),
  campaign: campaignStateSchema.describe('The sale alone, as before campaigns had kinds; read campaigns instead'),
  campaigns: publicCampaignsSchema,
  menu: z.array(publicMenuCategorySchema),
  openings: z.array(publicJobOpeningSchema),
  upcomingSessions: z.array(sessionSchema),
  locations: z.array(publicLocationSchema),
  assets: z.record(z.string(), publicAssetSchema),
  disabledModules: z.array(z.enum(GYM_MODULE_KEYS)).default([]).describe('Modules Bulk switched off for this gym: their data is not in this payload and their links and blocks are not drawn'),
  preview: z.boolean(),
});
export type PublicSiteDto = z.infer<typeof publicSiteSchema>;

export const memberHomeResponseSchema = z.object({
  cards: z.array(z.enum(MEMBER_HOME_CARDS)).describe('The cards to draw, in order: switched-off modules and cards the design does not allow are already gone'),
  welcome: localizedText.describe('Markdown per locale, as the content team typed it'),
  hero: publicAssetSchema.nullable(),
  quickLinks: z.array(quickLinkSchema),
  header: z.enum(MEMBER_HEADER_VARIANTS),
  design: siteDesignSchema.nullable().describe("The gym's design, for its fonts and shape; null keeps Bulk's own"),
  fonts: z.record(z.string(), publicAssetSchema).describe("The design's uploaded font files, by asset id"),
  currency: z.string().describe("The gym's currency, for the bar menu's prices"),
  openingHours: openingHoursSchema.nullable().describe('Only while the opening hours card is on'),
  trainers: z
    .array(z.object({ id: idSchema, firstName: z.string(), lastName: z.string(), firstNameLocalized: z.record(z.string(), z.string()), lastNameLocalized: z.record(z.string(), z.string()), photo: publicAssetSchema.nullable() }))
    .describe('Only while the trainers card is on'),
  menu: z.array(publicMenuCategorySchema).describe('Only while the bar menu card is on'),
});
export type MemberHomeDto = z.infer<typeof memberHomeResponseSchema>;

export const portalLookSchema = z.object({
  design: siteDesignSchema,
  branding: brandingSchema,
  logo: publicAssetSchema.nullable().describe("The gym's logo picture, when it has one"),
  fonts: z.record(z.string(), publicAssetSchema).describe("The design's uploaded font files, by asset id"),
});
export type PortalLookDto = z.infer<typeof portalLookSchema>;
